// Maps a failed POST /orders into checkout's INLINE failure state (#156). Only the
// contracted failures get the inline treatment — the exact set useOrderApi's
// suppression predicate hides from useApi's global toast: the five payment codes,
// the two 409s (#441, PR 339) and the stock 412 (#443). Everything else
// (400/403/404, every other 412, unknown 402 codes, bodyless 500, network) returns
// undefined: those failures already surfaced through useApi's full-fidelity
// handlers, so the caller renders nothing for them.
//
// Modeled on PaymentRequired/_helpers/paymentMethodError.ts: duck-typed ApiFailure,
// { error, message } body parsed from ApiError.detail, server message wins.

import {
    PAYMENT_DECLINED,
    PAYMENT_HELD,
    PAYMENT_UNAVAILABLE,
    PAYMENT_UNCONFIRMED,
} from "@models/paymentMethod";
import {
    ORDER_SAVE_FAILED,
    STOCK_CHANGED_DURING_CHECKOUT,
    stockShortfallItems,
    type StockShortfallItem,
} from "@models/order";
import { asApiFailure, parseFailureBody } from "@utils/apiFailure";

// Which kinds may invite a retry, and why the others must not (isDoNotRetry):
//   declined        — 402 payment_declined. The vendor rejected the card and no money
//                     moved; fixing the card and placing again is safe. The server
//                     puts the cart back with the same lines.
//   unavailable     — 402 payment_unavailable (MB-05, #439). The gateway refused OUR
//                     request (merchant account or gateway down): the card was never
//                     judged and no money moved. The server releases the cart claim as
//                     for a decline; trying again later is the fix, not another card.
//   conflict       — 409. Another checkout of this cart (another tab, or an earlier
//                     attempt still going through) holds it; this attempt reached no
//                     gateway. A retry cannot double-charge — the server refuses it the
//                     same way — but the shopper should look at Orders first.
//   stockChanged    — 409 stock_changed_during_checkout (PR 339). The stock reservation
//                     was refused, yet the stock was back by the server's re-read: it
//                     moved during this checkout. Refused before any charge, with the cart
//                     as it was; the page re-reads the cart and its stock, and the shopper
//                     places the order again.
//   outOfStock      — 412, lines above their vendor's stock (named in `items`). Refused
//                     before any charge; the cart is kept, and the fix (quantity or
//                     vendor) is on those lines.
//   unconfirmed     — 402 payment_unconfirmed. The charge outcome is UNKNOWN; a retry
//                     risks a double charge.
//   held            — 402 payment_held. The vendor is holding a REAL transaction;
//                     the server message carries the transaction id as a support
//                     reference. A retry would charge again on top of the hold.
//   orderSaveFailed — 500 order_save_failed. The charge SUCCEEDED but no order was
//                     created; the server message carries the transaction id. A retry
//                     is a guaranteed second charge.
type PaymentOutcomeKind = "declined" | "unconfirmed" | "held" | "orderSaveFailed";

export type CheckoutOrderErrorKind =
    | PaymentOutcomeKind
    | "unavailable"
    | "conflict"
    | "stockChanged"
    | "outOfStock";

/**
 * True for the kinds after which money may have moved: another attempt risks a second
 * charge. They are also exactly the outcomes after which the server keeps the cart's
 * checkout claim (#441), so the cart is empty.
 */
export const isDoNotRetry = (kind: CheckoutOrderErrorKind): boolean =>
    kind === "unconfirmed" || kind === "held" || kind === "orderSaveFailed";

export interface CheckoutOrderError {
    kind: CheckoutOrderErrorKind;
    message: string;
    // outOfStock only: the cart lines the 412 names, for the page to mark row by row.
    items?: StockShortfallItem[];
}

// The floor, used only when the server sent no usable `message`. The server's copy
// always wins when present — for held/orderSaveFailed it carries the vendor
// transaction id the customer must quote to support.
const DECLINED_FALLBACK =
    "Your payment was declined. Please check your card details, or try a different card, and place the order again.";

const UNCONFIRMED_FALLBACK =
    "We couldn't confirm your payment. Please don't try again — contact support so we can verify it for you.";

const HELD_FALLBACK =
    "Your payment is being reviewed by our payment provider and hasn't completed yet. Please don't try again — contact support and we'll sort it out.";

const ORDER_SAVE_FAILED_FALLBACK =
    "Your payment went through, but we couldn't finish creating your order. Please don't try again — contact support and we'll complete it for you.";

// The 409s', the stock 412's and payment_unavailable's copy is the UI's own: the server's
// sentences say what was refused, not what the shopper should do about it (and
// payment_unavailable's says nothing of the order).
const UNAVAILABLE_MESSAGE =
    "The payment service isn't available right now. Nothing was charged and your order wasn't placed. Try again in a few minutes.";

const CONFLICT_MESSAGE =
    "This cart is already being checked out, in another tab or by an earlier attempt that is still going through. No payment was taken for this attempt. Check your orders before trying again.";

const STOCK_CHANGED_MESSAGE =
    "The stock changed while your order was being placed, so it wasn't placed and no payment was taken. Check your items and place your order again.";

// Names each product once: two variant lines of one product share its name.
const outOfStock = (items: StockShortfallItem[]): CheckoutOrderError => ({
    kind: "outOfStock",
    message: `The selected vendor doesn't have enough stock for: ${[
        ...new Set(items.map((item) => item.productName)),
    ].join(", ")}. Your order wasn't placed and no payment was taken. Go back to your cart to lower the quantity or switch to another vendor.`,
    items,
});

/**
 * The stock failure with one line unmarked — the shopper fixed it on the checkout page —
 * re-worded for the lines left, or null once none is. Any other failure, or a line it
 * does not mark, comes back as it was.
 */
export const withoutShortfallLine = (
    failure: CheckoutOrderError,
    itemId: string,
): CheckoutOrderError | null => {
    const items = failure.items;

    if (!items?.some((item) => item.shoppingCartItemId === itemId)) {
        return failure;
    }

    const rest = items.filter((item) => item.shoppingCartItemId !== itemId);

    return rest.length > 0 ? outOfStock(rest) : null;
};

export const checkoutOrderError = (
    error: unknown,
): CheckoutOrderError | undefined => {
    const failure = asApiFailure(error);

    if (!failure) {
        return undefined;
    }

    // POST /orders' two 409s: the coded stock change (PR 339), and the claim's plain
    // string. Anything not carrying the code — the claim's sentence, an unknown body, none
    // — stays the claim conflict.
    if (failure.status === 409) {
        return parseFailureBody(failure.detail)?.error === STOCK_CHANGED_DURING_CHECKOUT
            ? { kind: "stockChanged", message: STOCK_CHANGED_MESSAGE }
            : { kind: "conflict", message: CONFLICT_MESSAGE };
    }

    // The one coded 412 (`insufficient_stock`); every other 412 is a plain string.
    if (failure.status === 412) {
        const items = stockShortfallItems(failure.detail);

        return items ? outOfStock(items) : undefined;
    }

    const body = parseFailureBody(failure.detail);

    if (failure.status === 402 && body?.error === PAYMENT_UNAVAILABLE) {
        return { kind: "unavailable", message: UNAVAILABLE_MESSAGE };
    }

    const kind = failureKind(failure.status, body?.error);

    if (!kind) {
        return undefined;
    }

    return {
        kind,
        message: body?.message.trim() ? body.message : fallbackCopy(kind),
    };
};

// Failure codes from the POST /orders contract. The three 402 codes never arrive on
// any other status, and order_save_failed only ever arrives on 500 — the pairing is
// checked strictly so a mismatched status/code combination falls back to the toast
// rather than rendering payment copy for something that isn't the contracted failure.
const failureKind = (
    status: number,
    code: string | undefined,
): PaymentOutcomeKind | undefined => {
    if (status === 402) {
        switch (code) {
            case PAYMENT_DECLINED:
                return "declined";
            case PAYMENT_UNCONFIRMED:
                return "unconfirmed";
            case PAYMENT_HELD:
                return "held";
            default:
                return undefined;
        }
    }

    if (status === 500 && code === ORDER_SAVE_FAILED) {
        return "orderSaveFailed";
    }

    return undefined;
};

const fallbackCopy = (kind: PaymentOutcomeKind): string => {
    switch (kind) {
        case "declined":
            return DECLINED_FALLBACK;
        case "unconfirmed":
            return UNCONFIRMED_FALLBACK;
        case "held":
            return HELD_FALLBACK;
        case "orderSaveFailed":
            return ORDER_SAVE_FAILED_FALLBACK;
    }
};
